import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import {
  formatNumberWithCommas,
  formatPercentage,
  parseFormattedNumber,
} from "../../utils/formatters";
import closeSvg from "../../assets/close.svg";
import "./MeetingReportModal.css";
import { handleAuthError, handleGenericError } from "../../utils/errorHandler";

function MeetingReportModal({
  isOpen,
  onClose,
  recruit,
  meeting,
  onScheduleNewMeeting,
  onAdvanceStage,
  onDocumentSent,
}) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [meetingOccurred, setMeetingOccurred] = useState(true);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showOfferProposal, setShowOfferProposal] = useState(false);
  const [includeOverride, setIncludeOverride] = useState(false);
  const [includeMd, setIncludeMd] = useState(false);
  const [additionalTerms, setAdditionalTerms] = useState("");

  // Templates
  const [commissionTemplates, setCommissionTemplates] = useState([]);
  const [overrideTemplates, setOverrideTemplates] = useState([]);
  const [mdTemplates, setMdTemplates] = useState([]);

  // Compensation data
  const [compensationData, setCompensationData] = useState({
    commissionScale: Array.from({ length: 8 }, (_, index) => ({
      id: index + 1,
      revenue: "",
      percentage: "",
    })),
    selectedCommissionTemplate: "",
    overrideScale: Array.from({ length: 8 }, (_, index) => ({
      id: index + 1,
      revenue: "",
      percentage: "",
    })),
    selectedOverrideTemplate: "",
  });

  // Load templates when modal opens
  useEffect(() => {
    const loadTemplates = async () => {
      if (!isOpen) return;

      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;

        const [commissionRes, overrideRes, mdRes] = await Promise.all([
          fetch(getApiEndpoint("/v1/scales/default-scales/COMMISSION"), {
            headers: { Authorization: `Bearer ${jwt}` },
          }),
          fetch(getApiEndpoint("/v1/scales/default-scales/OVERRIDE"), {
            headers: { Authorization: `Bearer ${jwt}` },
          }),
          fetch(getApiEndpoint("/v1/scales/default-scales/MD"), {
            headers: { Authorization: `Bearer ${jwt}` },
          }),
        ]);

        if (commissionRes.ok) {
          const data = await commissionRes.json();
          setCommissionTemplates(data.data || []);
        }
        if (overrideRes.ok) {
          const data = await overrideRes.json();
          setOverrideTemplates(data.data || []);
        }
        if (mdRes.ok) {
          const data = await mdRes.json();
          setMdTemplates(data.data || []);
        }
      } catch (error) {}
    };

    loadTemplates();
  }, [isOpen, getVerifiedJWT]);

  const handleBackToNotes = () => {
    setCurrentPage(1);
  };

  const handleCommissionTemplateChange = e => {
    const templateId = e.target.value;
    setCompensationData(prev => ({
      ...prev,
      selectedCommissionTemplate: templateId,
    }));

    if (templateId) {
      const template = commissionTemplates.find(
        t => t.id === parseInt(templateId)
      );
      if (template?.scale_lines) {
        const newScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue: formatNumberWithCommas(line.revenue_amount.toString()),
          percentage: formatPercentage(line.percentage.toString()),
        }));
        setCompensationData(prev => ({
          ...prev,
          commissionScale: newScale,
        }));
      }
    }
  };

  const handleOverrideTemplateChange = e => {
    const templateId = e.target.value;
    setCompensationData(prev => ({
      ...prev,
      selectedOverrideTemplate: templateId,
    }));

    if (templateId) {
      const template = overrideTemplates.find(
        t => t.id === parseInt(templateId)
      );
      if (template?.scale_lines) {
        const newScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue: formatNumberWithCommas(line.revenue_amount.toString()),
          percentage: formatPercentage(line.percentage.toString()),
        }));
        setCompensationData(prev => ({
          ...prev,
          overrideScale: newScale,
        }));
      }
    }
  };

  const handleMdTemplateChange = e => {
    const templateId = e.target.value;
    setCompensationData(prev => ({
      ...prev,
      selectedMdTemplate: templateId,
    }));

    if (templateId) {
      const template = mdTemplates.find(t => t.id === parseInt(templateId));
      if (template?.scale_lines) {
        const newScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue: formatNumberWithCommas(line.revenue_amount.toString()),
          percentage: formatPercentage(line.percentage.toString()),
        }));
        setCompensationData(prev => ({
          ...prev,
          overrideScale: newScale, // Using same array for both
        }));
      }
    }
  };

  const handleCommissionChange = (id, field, value) => {
    const formattedValue =
      field === "revenue"
        ? formatNumberWithCommas(value)
        : formatPercentage(value);
    setCompensationData(prev => ({
      ...prev,
      commissionScale: prev.commissionScale.map(item =>
        item.id === id ? { ...item, [field]: formattedValue } : item
      ),
    }));
  };

  const handleOverrideChange = (id, field, value) => {
    const formattedValue =
      field === "revenue"
        ? formatNumberWithCommas(value)
        : formatPercentage(value);
    setCompensationData(prev => ({
      ...prev,
      overrideScale: prev.overrideScale.map(item =>
        item.id === id ? { ...item, [field]: formattedValue } : item
      ),
    }));
  };

  const addCommissionRow = () => {
    if (compensationData.commissionScale.length >= 16) return;
    const newId =
      Math.max(...compensationData.commissionScale.map(item => item.id)) + 1;
    setCompensationData(prev => ({
      ...prev,
      commissionScale: [
        ...prev.commissionScale,
        { id: newId, revenue: "", percentage: "" },
      ],
    }));
  };

  const removeCommissionRow = id => {
    if (compensationData.commissionScale.length > 1) {
      setCompensationData(prev => ({
        ...prev,
        commissionScale: prev.commissionScale.filter(item => item.id !== id),
      }));
    }
  };

  const addOverrideRow = () => {
    if (compensationData.overrideScale.length >= 16) return;
    const newId =
      Math.max(...compensationData.overrideScale.map(item => item.id)) + 1;
    setCompensationData(prev => ({
      ...prev,
      overrideScale: [
        ...prev.overrideScale,
        { id: newId, revenue: "", percentage: "" },
      ],
    }));
  };

  const removeOverrideRow = id => {
    if (compensationData.overrideScale.length > 1) {
      setCompensationData(prev => ({
        ...prev,
        overrideScale: prev.overrideScale.filter(item => item.id !== id),
      }));
    }
  };

  const handleScheduleFollowUp = async () => {
    if (!recruit || !meeting) return;
    await handleSubmit(true);
    if (onScheduleNewMeeting) {
      // Close with meeting data so parent can do optimistic update
      setNotes("");
      setMeetingOccurred(true);
      setShowOfferProposal(false);
      setCurrentPage(1);
      setIncludeOverride(false);
      setIncludeMd(false);
      setAdditionalTerms("");
      setCompensationData({
        commissionScale: Array.from({ length: 8 }, (_, index) => ({
          id: index + 1,
          revenue: "",
          percentage: "",
        })),
        selectedCommissionTemplate: "",
        overrideScale: Array.from({ length: 8 }, (_, index) => ({
          id: index + 1,
          revenue: "",
          percentage: "",
        })),
        selectedOverrideTemplate: "",
        selectedMdTemplate: "",
      });
      // Pass meeting data back to parent
      onClose(true, { meeting_date: meeting.time });
      setTimeout(() => onScheduleNewMeeting(recruit, meetingOccurred), 100);
    }
  };

  const handleSubmit = async (skipClose = false) => {
    if (!recruit || !meeting) return;

    setLoading(true);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        handleAuthError("Invalid jwt");
        return;
      }

      const response = await fetch(
        getApiEndpoint(`/v2/recruiting/leads/${recruit.id}/report-meeting`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            meeting_occurred: meetingOccurred,
            notes: notes.trim(),
            meeting_date: meeting.time,
            ...(showOfferProposal && {
              offer_proposal: {
                commission_scale: compensationData.commissionScale
                  .filter(item => item.revenue || item.percentage)
                  .map(item => ({
                    revenue:
                      parseFloat(parseFormattedNumber(item.revenue)) || 0,
                    percentage:
                      parseFloat(parseFormattedNumber(item.percentage)) || 0,
                  })),
                has_override: includeOverride && !includeMd,
                override_scale:
                  includeOverride && !includeMd
                    ? compensationData.overrideScale
                        .filter(item => item.revenue || item.percentage)
                        .map(item => ({
                          revenue:
                            parseFloat(parseFormattedNumber(item.revenue)) || 0,
                          percentage:
                            parseFloat(parseFormattedNumber(item.percentage)) ||
                            0,
                        }))
                    : [],
                has_md: includeMd,
                md_scale: includeMd
                  ? compensationData.overrideScale
                      .filter(item => item.revenue || item.percentage)
                      .map(item => ({
                        revenue:
                          parseFloat(parseFormattedNumber(item.revenue)) || 0,
                        percentage:
                          parseFloat(parseFormattedNumber(item.percentage)) ||
                          0,
                      }))
                  : [],
                additional_terms: additionalTerms.trim(),
              },
            }),
          }),
        }
      );

      if (response.ok) {
        if (!skipClose) {
          // If meeting occurred, advance to next stage
          if (meetingOccurred && onAdvanceStage && recruit) {
            await onAdvanceStage(recruit);
          }

          showSuccess(
            meetingOccurred
              ? "Meeting report saved successfully"
              : "Meeting marked as not occurred"
          );
          setNotes("");
          setMeetingOccurred(true);
          setShowOfferProposal(false);
          setCurrentPage(1);
          setIncludeOverride(false);
          setIncludeMd(false);
          setAdditionalTerms("");
          setCompensationData({
            commissionScale: Array.from({ length: 8 }, (_, index) => ({
              id: index + 1,
              revenue: "",
              percentage: "",
            })),
            selectedCommissionTemplate: "",
            overrideScale: Array.from({ length: 8 }, (_, index) => ({
              id: index + 1,
              revenue: "",
              percentage: "",
            })),
            selectedOverrideTemplate: "",
            mdScale: Array.from({ length: 8 }, (_, index) => ({
              id: index + 1,
              revenue: "",
              percentage: "",
            })),
            selectedMdTemplate: "",
          });
          // Pass back the meeting date so parent can do optimistic update
          onClose(true, { meeting_date: meeting.time });
        }
      } else {
        showError("Failed to save meeting report");
      }
    } catch (error) {
      handleGenericError(error, "Failed to save meeting report");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNotes("");
    setMeetingOccurred(true);
    setShowOfferProposal(false);
    setCurrentPage(1);
    setIncludeOverride(false);
    setIncludeMd(false);
    setAdditionalTerms("");
    setCompensationData({
      commissionScale: Array.from({ length: 8 }, (_, index) => ({
        id: index + 1,
        revenue: "",
        percentage: "",
      })),
      selectedCommissionTemplate: "",
      overrideScale: Array.from({ length: 8 }, (_, index) => ({
        id: index + 1,
        revenue: "",
        percentage: "",
      })),
      selectedOverrideTemplate: "",
      selectedMdTemplate: "",
    });
    onClose(false);
  };

  if (!isOpen) return null;

  return (
    <div className="meeting-report-modal-overlay" onClick={handleClose}>
      <div
        className="meeting-report-modal-content"
        onClick={e => e.stopPropagation()}
      >
        <div className="meeting-report-modal-header">
          <h2>Log Meeting</h2>
          <button className="meeting-report-close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="meeting-report-modal-body">
          {currentPage === 1 && (
            <>
              {recruit && (
                <div className="meeting-report-info">
                  <p className="recruit-name">{recruit.name}</p>
                  {meeting && (
                    <p className="meeting-time">
                      Meeting scheduled:{" "}
                      {new Date(meeting.time).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}{" "}
                      at{" "}
                      {new Date(meeting.time).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  )}
                </div>
              )}

              <div className="meeting-occurred-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={meetingOccurred}
                    onChange={e => setMeetingOccurred(e.target.checked)}
                  />
                  <span>Meeting occurred</span>
                </label>
              </div>

              <div className="notes-section">
                <label htmlFor="meeting-notes">Meeting Notes</label>
                <textarea
                  id="meeting-notes"
                  className="meeting-notes-textarea"
                  placeholder="Add notes about the meeting..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={6}
                />
              </div>

              {/* COMMENTED OUT: Offer Proposal Toggle
              <div className="offer-proposal-toggle">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={showOfferProposal}
                    onChange={e => handleOfferProposalToggle(e.target.checked)}
                  />
                  <span>Add Offer Proposal</span>
                </label>
              </div>
              */}
            </>
          )}

          {currentPage === 2 && (
            <>
              <button
                type="button"
                className="back-button"
                onClick={handleBackToNotes}
              >
                <span className="material-icons" style={{ fontSize: "1.2rem" }}>
                  arrow_back
                </span>
                <span>Back to Notes</span>
              </button>

              <div className="page-title">
                <h3>Offer Proposal</h3>
                <p>Configure compensation details for {recruit?.name}</p>
              </div>

              <div className="compensation-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeOverride}
                    onChange={e => {
                      setIncludeOverride(e.target.checked);
                      if (e.target.checked) setIncludeMd(false);
                    }}
                  />
                  <span>Include Override</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeMd}
                    onChange={e => {
                      setIncludeMd(e.target.checked);
                      if (e.target.checked) setIncludeOverride(false);
                    }}
                  />
                  <span>Include Marketing Deal</span>
                </label>
              </div>

              <div className="compensation-grid-three">
                <div className="compensation-column">
                  <h4>Pay Scale</h4>

                  {commissionTemplates.length > 0 && (
                    <div className="template-selector">
                      <label>Use Default Scale Template</label>
                      <select
                        value={compensationData.selectedCommissionTemplate}
                        onChange={handleCommissionTemplateChange}
                      >
                        <option value="">Select a template...</option>
                        {commissionTemplates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="scale-table-container">
                    <table className="scale-table">
                      <thead>
                        <tr>
                          <th>Revenue ($)</th>
                          <th>Percentage (%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compensationData.commissionScale.map(item => (
                          <tr key={item.id}>
                            <td>
                              <input
                                type="text"
                                value={item.revenue}
                                onChange={e =>
                                  handleCommissionChange(
                                    item.id,
                                    "revenue",
                                    e.target.value
                                  )
                                }
                                placeholder="0.00"
                              />
                            </td>
                            <td className="percentage-cell">
                              <input
                                type="text"
                                value={item.percentage}
                                onChange={e =>
                                  handleCommissionChange(
                                    item.id,
                                    "percentage",
                                    e.target.value
                                  )
                                }
                                placeholder="0.0"
                              />
                              {compensationData.commissionScale.length > 1 && (
                                <button
                                  type="button"
                                  className="simple-remove-btn"
                                  onClick={() => removeCommissionRow(item.id)}
                                  title="Remove row"
                                >
                                  <img src={closeSvg} alt="Remove" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={addCommissionRow}
                    disabled={compensationData.commissionScale.length >= 16}
                  >
                    + Add Row{" "}
                    {compensationData.commissionScale.length >= 16
                      ? "(Max 16)"
                      : ""}
                  </button>
                </div>

                <div
                  className={`compensation-column ${!includeOverride && !includeMd ? "disabled" : ""}`}
                >
                  <h4>
                    {includeMd ? "Marketing Deal Scale" : "Override Scale"}
                  </h4>

                  {includeMd && mdTemplates.length > 0 && (
                    <div className="template-selector">
                      <label>Use Default MD Template</label>
                      <select
                        value={compensationData.selectedMdTemplate}
                        onChange={handleMdTemplateChange}
                        disabled={!includeMd}
                      >
                        <option value="">Select a template...</option>
                        {mdTemplates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {includeOverride &&
                    !includeMd &&
                    overrideTemplates.length > 0 && (
                      <div className="template-selector">
                        <label>Use Default Override Template</label>
                        <select
                          value={compensationData.selectedOverrideTemplate}
                          onChange={handleOverrideTemplateChange}
                          disabled={!includeOverride}
                        >
                          <option value="">Select a template...</option>
                          {overrideTemplates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                  {!includeOverride &&
                    !includeMd &&
                    (overrideTemplates.length > 0 ||
                      mdTemplates.length > 0) && (
                      <div className="template-selector">
                        <label>Use Default Template</label>
                        <select disabled>
                          <option value="">Select a template...</option>
                        </select>
                      </div>
                    )}

                  <div className="scale-table-container">
                    <table className="scale-table">
                      <thead>
                        <tr>
                          <th>Revenue ($)</th>
                          <th>Percentage (%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compensationData.overrideScale.map(item => (
                          <tr key={item.id}>
                            <td>
                              <input
                                type="text"
                                value={item.revenue}
                                onChange={e =>
                                  handleOverrideChange(
                                    item.id,
                                    "revenue",
                                    e.target.value
                                  )
                                }
                                placeholder="0.00"
                                disabled={!includeOverride && !includeMd}
                              />
                            </td>
                            <td className="percentage-cell">
                              <input
                                type="text"
                                value={item.percentage}
                                onChange={e =>
                                  handleOverrideChange(
                                    item.id,
                                    "percentage",
                                    e.target.value
                                  )
                                }
                                placeholder="0.0"
                                disabled={!includeOverride && !includeMd}
                              />
                              {compensationData.overrideScale.length > 1 && (
                                <button
                                  type="button"
                                  className="simple-remove-btn"
                                  onClick={() => removeOverrideRow(item.id)}
                                  title="Remove row"
                                  disabled={!includeOverride && !includeMd}
                                >
                                  <img src={closeSvg} alt="Remove" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={addOverrideRow}
                    disabled={
                      (!includeOverride && !includeMd) ||
                      compensationData.overrideScale.length >= 16
                    }
                  >
                    + Add Row{" "}
                    {compensationData.overrideScale.length >= 16
                      ? "(Max 16)"
                      : ""}
                  </button>
                </div>

                <div className="compensation-column">
                  <h4>Additional Terms or Bonuses</h4>
                  <textarea
                    className="additional-terms-textarea"
                    placeholder="Enter any additional terms, bonuses, or special conditions..."
                    value={additionalTerms}
                    onChange={e => setAdditionalTerms(e.target.value)}
                    rows={18}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="meeting-report-modal-footer">
          <div
            style={{
              width: "100%",
              marginBottom: "0.75rem",
              textAlign: "center",
            }}
          >
            <h4 style={{ fontSize: "0.95rem", margin: "0" }}>Next Step</h4>
          </div>
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "stretch",
              width: "100%",
            }}
          >
            <button
              className="btn btn-success"
              onClick={() => handleSubmit(false)}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Save
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleScheduleFollowUp}
              disabled={loading}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                justifyContent: "center",
              }}
            >
              <span className="material-icons" style={{ fontSize: "1.1rem" }}>
                event
              </span>
              <span>Save and Schedule</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MeetingReportModal;
